import React, { useCallback } from 'react';

import {
  updateDuplicateIdAction,
  updateRuleAction,
  updateStageAction,
} from 'app-components/flux/transaction-rules/transactionRuleActions';
import { useManager } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import type { ConditionsEnum } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { EDITING, ResolutionTypeEnum, VALIDATED } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';

import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';

import { RuleDuplciateDialog } from './RuleDuplicateDialog';
import { SourceHeading, TargetHeading } from './reviewConditions/Headings';
import { PayeeSourceCard } from './sourceConditions/PayeeSourceCard';
import { CategoryTargetCard } from './targetConditions/CategoryTargetCard';
import { PayeeTargetCard } from './targetConditions/PayeeTargetCard';

import { transactionRulesSelectors } from 'app-components/flux/transaction-rules/transactionRulesSlice';
import { tracker } from 'app-components/utils/core';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, { fromTxn }: any) => ({
  main: {
    display: 'flex',
    flex: 1,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  conditionsContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: !fromTxn ? 'row' : 'column',
    '> div+div': {
      borderLeft: !fromTxn ? `1px solid ${theme.palette.divider}` : 'none',
    },
  },
  conditions: {
    display: 'flex',
    flex: 1,
    padding: !fromTxn ? theme.spacing(2) : theme.spacing(0),
    fontWeight: 500,
    flexDirection: 'column',
    overflow: 'auto',
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  instructions: {
    padding: `0  ${theme.spacing(2)} ${theme.spacing(1)}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const RuleEditorContent: React.FC = () => {
  const { state, dispatch } = useStateContext();
  const manager = useManager();
  const { duplicateId, fromTxn, stage, editField } = state;
  const { fireAction } = manager;
  const isEditing = state.rule?.id ? true : false;

  const styleProps: any = { fromTxn };
  const { classes } = useStyles(styleProps);
  const originalRule = useAppSelector((state) => transactionRulesSelectors.selectById(state, duplicateId || ''));

  const handleDialogAction = useCallback(
    (value: ResolutionTypeEnum) => {
      tracker.track(tracker.events.txnRuleDuplicate, { userAction: value });

      switch (value) {
        case ResolutionTypeEnum.CLOSE:
          dispatch(updateDuplicateIdAction(undefined));
          dispatch(updateStageAction(EDITING));
          break;
        case ResolutionTypeEnum.EDIT:
          manager.reinit(originalRule);
          dispatch(updateDuplicateIdAction(undefined));
          dispatch(updateRuleAction(originalRule));
          dispatch(updateStageAction(EDITING));
          break;
        case ResolutionTypeEnum.REPLACE:
          dispatch(updateStageAction(VALIDATED));
          break;
        default:
          break;
      }
    },
    [dispatch, manager, originalRule],
  );

  if (fromTxn && editField == 'coaTargetCondition') {
    fireAction('showCondition', { condition: 'coaTargetCondition' as ConditionsEnum, value: true });
    fireAction('showCondition', { condition: 'payeeTarget' as ConditionsEnum, value: false });
  }

  if (fromTxn && editField == 'payeeTarget') {
    fireAction('showCondition', { condition: 'payeeTarget' as ConditionsEnum, value: true });
    fireAction('showCondition', { condition: 'coaTargetCondition' as ConditionsEnum, value: false });
  }

  return (
    <Box className={classes.conditionsContainer}>
      {fromTxn && (
        <Box className={classes.instructions} id='new-rule-dialog-instructions'>
          <Typography variant='paragraphRegular'>
            Once created, this rule will automatically update future matching transactions.
          </Typography>
        </Box>
      )}
      <Box className={classes.conditions}>
        <SourceHeading />
        <Box className={classes.cardColumn}>
          <PayeeSourceCard hideToggle />
        </Box>
      </Box>
      <Box className={classes.conditions}>
        <TargetHeading />
        <Box className={classes.cardColumn}>
          <PayeeTargetCard hideToggle={fromTxn && editField === 'payeeTarget'} />
          <CategoryTargetCard hideToggle={fromTxn && editField === 'coaTargetCondition'} isEditing={isEditing} />
        </Box>
      </Box>

      {duplicateId && stage !== VALIDATED && (
        <RuleDuplciateDialog onAction={handleDialogAction} originalRule={originalRule} />
      )}
    </Box>
  );
};

const RuleEditor: React.FC = () => {
  const { state } = useStateContext();
  const { fromTxn } = state;
  const styleProps: any = { fromTxn };
  const { classes } = useStyles(styleProps);

  return (
    <>
      {!fromTxn ? (
        <Card className={classes.main}>
          <RuleEditorContent />
        </Card>
      ) : (
        <RuleEditorContent />
      )}
    </>
  );
};

export default RuleEditor;
